import type { ExportData } from "./gridExport";

type Theme = "light" | "dark";

/**
 * Mirrors the palette in styles.css. Canvas can't read CSS custom properties, so these are
 * copied rather than computed — worth keeping in sync if the theme tokens ever change.
 *
 * Named fields rather than a string-keyed record, so each lookup is a definite `string` the
 * canvas API will accept.
 */
interface Palette {
  bg: string;
  card: string;
  text: string;
  textMuted: string;
  textFaint: string;
  accent: string;
  accentText: string;
  accentSoft: string;
  danger: string;
  dangerSoft: string;
  neutralSoft: string;
}

const PALETTE: Record<Theme, Palette> = {
  dark: {
    bg: "#070b10",
    card: "#0c131b",
    text: "#f4f9fd",
    textMuted: "#93a6b9",
    textFaint: "#6b7f93",
    accent: "#b8f43d",
    accentText: "#cdf76d",
    accentSoft: "rgba(184,244,61,0.15)",
    danger: "#ff8585",
    dangerSoft: "rgba(239,68,68,0.16)",
    neutralSoft: "rgba(255,255,255,0.05)",
  },
  light: {
    bg: "#f4f7fa",
    card: "#ffffff",
    text: "#0b1622",
    textMuted: "#5d7085",
    textFaint: "#8496a8",
    accent: "#84cc16",
    accentText: "#4a7c0d",
    accentSoft: "rgba(132,204,22,0.18)",
    danger: "#c62828",
    dangerSoft: "rgba(239,68,68,0.13)",
    neutralSoft: "rgba(11,22,34,0.045)",
  },
};

const NAME_COL = 190;
const CELL_W = 86;
const ROW_H = 56;
const HEADER_H = 104;
const COL_HEAD_H = 44;
const FOOTER_H = 40;
const PAD = 30;

/**
 * Draws the results grid straight onto a canvas — no DOM screenshot library, no external
 * dependency.
 *
 * Team crests are deliberately left out: they're served from provider-controlled hosts with no
 * guaranteed CORS headers, and a single cross-origin image would taint the canvas and make
 * `toBlob()` fail silently for the whole export. Short names read just as well at this size.
 *
 * Win and loss carry a glyph as well as a colour, so the exported image survives being viewed
 * in greyscale or by someone colour-blind.
 */
export function renderResultsImage(data: ExportData, theme: Theme = "dark"): HTMLCanvasElement {
  const c = PALETTE[theme];
  const width = PAD * 2 + NAME_COL + CELL_W * data.roundLabels.length;
  const height = HEADER_H + COL_HEAD_H + ROW_H * data.rows.length + FOOTER_H + PAD;

  // Cap the pixel ratio: a 3× canvas of a 40-round season is enormous and slow to encode.
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(width * dpr);
  canvas.height = Math.round(height * dpr);
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;

  const ctx = canvas.getContext("2d")!;
  ctx.scale(dpr, dpr);

  const font = (weight: number, size: number) =>
    `${weight} ${size}px system-ui, -apple-system, "Segoe UI", sans-serif`;

  ctx.fillStyle = c.bg;
  ctx.fillRect(0, 0, width, height);

  /* Title block */
  ctx.fillStyle = c.text;
  ctx.font = font(700, 28);
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  ctx.fillText(data.title, PAD, 28);

  ctx.fillStyle = c.textMuted;
  ctx.font = font(500, 14);
  ctx.fillText(data.subtitle, PAD, 64);

  let y = HEADER_H;

  /* Column headers */
  ctx.font = font(700, 12);
  ctx.fillStyle = c.textFaint;
  ctx.textAlign = "center";
  data.roundLabels.forEach((label, i) => {
    ctx.fillText(label.toUpperCase(), PAD + NAME_COL + i * CELL_W + CELL_W / 2, y + 14);
  });
  y += COL_HEAD_H;

  /* Rows */
  for (const row of data.rows) {
    ctx.fillStyle = c.card;
    roundedRect(ctx, PAD, y + 3, width - PAD * 2, ROW_H - 6, 13);
    ctx.fill();

    const cy = y + ROW_H / 2;

    // Avatar
    ctx.fillStyle = row.status === "out" ? c.neutralSoft : c.accent;
    ctx.beginPath();
    ctx.arc(PAD + 24, cy, 15, 0, Math.PI * 2);
    ctx.fill();

    const initials = row.displayName
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("");
    ctx.fillStyle = row.status === "out" ? c.textFaint : "#08110b";
    ctx.font = font(700, 12);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(initials, PAD + 24, cy + 0.5);

    // Name and status
    ctx.textAlign = "left";
    ctx.fillStyle = row.status === "out" ? c.textMuted : c.text;
    ctx.font = font(600, 14);
    ctx.fillText(truncate(ctx, row.displayName, NAME_COL - 70), PAD + 50, cy - 7);

    ctx.fillStyle = row.status === "alive" ? c.accentText : c.danger;
    ctx.font = font(700, 10);
    ctx.fillText(row.status === "alive" ? "ALIVE" : "OUT", PAD + 50, cy + 9);

    // Cells
    row.cells.forEach((cell, i) => {
      const x = PAD + NAME_COL + i * CELL_W + 5;
      const w = CELL_W - 10;
      const cellY = y + 8;
      const h = ROW_H - 16;

      const fill =
        cell.outcome === "win" && cell.label
          ? c.accentSoft
          : cell.outcome === "loss" && cell.label
            ? c.dangerSoft
            : c.neutralSoft;

      ctx.fillStyle = fill;
      roundedRect(ctx, x, cellY, w, h, 9);
      ctx.fill();

      const centerY = cellY + h / 2;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      if (cell.hidden) {
        ctx.fillStyle = c.textFaint;
        ctx.font = font(700, 15);
        ctx.fillText("•••", x + w / 2, centerY);
        return;
      }
      if (!cell.label) {
        ctx.fillStyle = c.textFaint;
        ctx.font = font(600, 14);
        ctx.fillText("—", x + w / 2, centerY);
        return;
      }

      ctx.fillStyle =
        cell.outcome === "win" ? c.accentText : cell.outcome === "loss" ? c.danger : c.textMuted;
      ctx.font = font(700, 13);
      ctx.fillText(cell.label.slice(0, 4).toUpperCase(), x + w / 2, centerY + 1);

      // Redundant glyph so outcome survives greyscale.
      if (cell.outcome === "win") drawTick(ctx, x + w - 11, cellY + 9, c.accentText);
      if (cell.outcome === "loss") drawCross(ctx, x + w - 11, cellY + 9, c.danger);
    });

    y += ROW_H;
  }

  /* Footer */
  ctx.fillStyle = c.textFaint;
  ctx.font = font(500, 11);
  ctx.textAlign = "left";
  ctx.textBaseline = "alphabetic";
  ctx.fillText(
    `Last Man Standing · ${data.generatedAt.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    })}`,
    PAD,
    height - 16,
  );

  return canvas;
}

function drawTick(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.beginPath();
  ctx.moveTo(x - 3.5, y);
  ctx.lineTo(x - 1, y + 2.5);
  ctx.lineTo(x + 3.5, y - 3);
  ctx.stroke();
}

function drawCross(ctx: CanvasRenderingContext2D, x: number, y: number, color: string) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 2;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(x - 3, y - 3);
  ctx.lineTo(x + 3, y + 3);
  ctx.moveTo(x + 3, y - 3);
  ctx.lineTo(x - 3, y + 3);
  ctx.stroke();
}

/** Trims to fit a pixel width, with an ellipsis — long usernames must not run into the grid. */
function truncate(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let result = text;
  while (result.length > 1 && ctx.measureText(`${result}…`).width > maxWidth) {
    result = result.slice(0, -1);
  }
  return `${result}…`;
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

export function downloadCanvasAsPng(canvas: HTMLCanvasElement, filename: string): Promise<void> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Couldn't generate the image."));
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      // Revoking immediately can cancel the download in some browsers; one tick is enough.
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      resolve();
    }, "image/png");
  });
}
